/**
 * MarginTrend — margin by INVOICE month, beside the ring showing what the cost is made of.
 *
 * This is the periodisation Finance recognises and the one that reconciles: the same lines
 * bucketed on despatch read +13.8% against Sage where the invoice date reads −0.6%, because
 * FM invoices in batches after it ships and that lag is the entire gap.
 *
 * There is no delivered/open divider here, and its absence is the point. The order book's
 * calendar has to draw one because it spans work that has not shipped; every bar here is
 * billed, so the GM% line is a fact end to end.
 *
 * ⭐ AND THERE IS NO PART MONTH EITHER (23 Aug 2026). This file used to draw the month being
 * billed right now at part height behind a 'billing now' divider, with a caption explaining
 * that the bar was short because the month had not finished. That was the wrong answer to
 * the right observation: an open month is not margin at all, so the server no longer serves
 * one and the divider went with it. The exclusion is stated once, above the cards, where the
 * totals it changes are — not annotated onto a bar the reader has already misread.
 *
 * ⭐ AND THE RING DRAWS AT GROUP NOW (24 Aug 2026). It used to read one basis at a time, so a
 * Group node — Kirkstall's job actual beside Oracle's quote — had components that tied to
 * neither cost and the ring refused. The fact serves one cost, every row decomposes whichever
 * basis priced it, and the five slots tie at every node. The chip says whose cost it is.
 *
 * Clicking a month holds the breakdown to it; clicking it again releases it.
 */

import React from 'react'
import { AlertCircle } from 'lucide-react'
import InfoBanner from './InfoBanner'
import MarginBreakdownChart from './kit/charts/MarginBreakdownChart'
import SumCheckBanner from './kit/SumCheckBanner'
import OrderBookCostMix from './OrderBookCostMix'
import type { useMarginTimeline } from '../hooks/useMargin'
import { capBars, toBars, ONE_COST } from '../viewmodels/orderBook'
import { periodHeading } from './kit/cellFormat'
import { MONTH_KEY, MARGIN_COPY } from '../viewmodels/marginFact'
import { costNote } from '../viewmodels/marginCoverage'
import type { DrillRow, Site } from '../types/metrics'
import type { MarginTimeline } from '../types/margin'

/** Chronological order is the point, so the axis caps by RECENCY, not by value: the tail we
 * drop is the oldest months, never a hole in the middle of the trend. */
const MONTHS_ON_AXIS = 24

/** The caveat that belongs under the ring wherever Oracle is in it. Its quote records ONE
 * process figure, so the Machine/Labour split is ours, not Red Ant's. Shown by presence of
 * the lens rather than by a basis toggle: at Group the ring holds both entities and the
 * caveat still applies to the part of it that is Oracle's. */
function OracleSplitNote(): React.JSX.Element {
  return (
    <p>
      Oracle&apos;s Machine and Labour are <strong className="font-semibold text-mut">apportioned</strong>{' '}
      across the routing by work centre. The quote records one process figure, and Red Ant books
      its people as work centres rather than as a rate — so the total is quoted and the split
      between these two is modelled, not recorded.
    </p>
  )
}

interface Props {
  /** The timeline query, owned by MarginExplorer — the header cards above the tabs read the
   * same payload, so the cards and this calendar can never cover different work. */
  query: ReturnType<typeof useMarginTimeline>
  site: Site
  selected: string | null
  onSelect: (month: string | null) => void
}

function Calendar({ data, selected, onSelect }: {
  data: MarginTimeline
  selected: string | null
  onSelect: (month: string | null) => void
}): React.JSX.Element {
  const shown = data.rows.slice(-MONTHS_ON_AXIS)
  const { bars } = capBars(toBars(shown, MONTH_KEY, ONE_COST), shown.length)
  const onBarClick = (row: DrillRow): void => {
    const month = String(row[MONTH_KEY])
    onSelect(month === selected ? null : month)
  }
  return (
    <>
      <div className="px-2 pt-1">
        <MarginBreakdownChart
          bars={bars}
          label="Margin by invoice month"
          marginLine
          onBarClick={onBarClick}
        />
      </div>
      <p className="border-t border-line2 px-4 py-2 text-[11.5px] text-mut2">
        {MARGIN_COPY.caption}
        {data.rows.length > shown.length && ` Showing the most recent ${shown.length} of ${data.rows.length} months.`}
        {selected && (
          <>
            {' '}The breakdown is held to <span className="font-semibold text-ink">{periodHeading(selected)}</span> —
            click that month again to release it.
          </>
        )}
      </p>
      <SumCheckBanner sumCheck={data.sum_check} />
    </>
  )
}

export default function MarginTrend({
  query, site, selected, onSelect,
}: Props): React.JSX.Element {
  const data = query.data
  if (query.error) {
    return <InfoBanner variant="error" icon={AlertCircle} title="Margin trend unavailable" message={query.error.message} />
  }
  if (!data) {
    return <div role="status" className="animate-pulse rounded-xl border border-line px-4 py-6 text-center text-xs text-mut2">Loading the invoice calendar…</div>
  }
  if (data.rows.length === 0) {
    return <InfoBanner variant="info" message={`${MARGIN_COPY.empty} ${site} in this window.`} />
  }
  const oracle = data.total.lenses.some((lens) => lens.basis === 'quote_estimated')
  return (
    <div className="grid gap-2.5 lg:grid-cols-[minmax(0,1fr)_260px]">
      <div className="rounded-xl border border-line bg-panel">
        <div className="flex flex-wrap items-center gap-2.5 px-4 py-2.5 border-b border-line2 text-[13px] font-semibold text-ink">
          {MARGIN_COPY.calendar}
          <span className="font-mono text-[10px] font-normal text-mut2">
            revenue vs cost per month · click a month to take it into the breakdown
          </span>
        </div>
        <Calendar data={data} selected={selected} onSelect={onSelect} />
      </div>
      <OrderBookCostMix
        totals={data.total.cost}
        source={costNote(data.total)}
        refusal="Nothing at this level carries a cost, so there is nothing to break down."
        footnote={oracle ? <OracleSplitNote /> : undefined}
      />
    </div>
  )
}
